import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ResponseDeciderService } from 'src/app/shared/services/responseDecider/responseDecider.service';
import { LoggerService } from '../logger.service';
import { LogDataService } from '../logData.service';
import * as _ from 'lodash';

/**
 * Auth interceptor
 */
@Injectable()
export class HttpMessageInterceptor implements HttpInterceptor {
  constructor(
    private toaster: ToastService,
    private deciderService: ResponseDeciderService,
    private loggerService: LoggerService,
    private logDataService: LogDataService
  ) {}
  /**
   * interceptor
   * @param req http request
   * @param next http handler
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let self = this;
    const success: string = req.headers.get('x-success-message');
    const error: string = req.headers.get('x-error-message');
    const deciderKey: string = req.headers.get('x-response-decider-key');
    const dontLog: string = req.headers.get('x-dont-log');
    const requestId: string = req.headers.get('x-request-id');

    return next.handle(req).pipe(
      tap((evt) => {        
        if (evt instanceof HttpResponse) {
          if (deciderKey) {            
            if (this.deciderService.IsError(deciderKey, evt)) {
              const safeError = this.getSafeErrorMessage(error, evt.body);
              this.toaster.error(null, safeError);
              this.logApiFailureException(req, safeError, dontLog, evt.body);
            } else if (success !== 'undefined' && success !== null) {
              this.toaster.success(null, success);
            }
          } else if (evt.ok && success) {
            this.toaster.success(null, success);
          }
        }
      }),
      catchError((err: any) => {
        if (dontLog === 'true') {
          return of(err);
        }        
        if (err instanceof HttpErrorResponse && err.status !== 200) {
          const safeError = this.getSafeErrorMessage(error, err);
          try {
            if (safeError) {
              this.toaster.error(null, safeError);
            }
            this.logApiFailureException(req, safeError, dontLog, err);
          } catch (e) {
            this.toaster.error(null, safeError);
            this.logApiFailureException(req, safeError, dontLog, {
              exceptions: e,
              apiError: err,
            });
          }
          return of(new HttpResponse({ status: 500, body: err }));
        }

        // this is for APIs that do not return graceful json
        // if (success && err.status === 200) {
        if (err.status === 200) {
          if (success) {
            this.toaster.success(null, success);
          }
          return of(new HttpResponse({ status: 200, body: err.json }));
        }
        
        // this.logApiFailureException(req, error, dontLog, err);
        return of(err);
      }),
      finalize(() => {
        self.logDataService.deleteLogData(requestId);
      })
    );
  }

  private getSafeErrorMessage(message: string, apiError: any): string {
    if (message && message.trim().length > 0) {
      return message;
    }

    if (apiError instanceof HttpErrorResponse) {
      if (typeof apiError.error === 'string' && apiError.error.trim().length > 0) {
        return apiError.error;
      }

      if (apiError.message && apiError.message.trim().length > 0) {
        return apiError.message;
      }
    }

    return 'API request failed';
  }

  logApiFailureException(req, error, dontLog, apiError) {
    let self = this;
    if (dontLog === 'true') {
      return;
    }
    const errorData: Error = new Error(error);
    const requestId: string = req.headers.get('x-request-id');

    const httpError = apiError instanceof HttpErrorResponse ? apiError : null;
    const diagnostics = {
      requestId,
      method: req.method,
      urlWithParams: req.urlWithParams,
      hasAuthorizationHeader: req.headers.has('Authorization'),
      hasPermissionsHeader: req.headers.has('x-permissions'),
      status: httpError ? httpError.status : apiError?.status,
      statusText: httpError ? httpError.statusText : apiError?.statusText,
      apiMessage: httpError ? httpError.message : apiError?.message,
      serverCorrelationId:
        (httpError?.headers && (httpError.headers.get('x-correlation-id') || httpError.headers.get('x-ms-request-id'))) ||
        undefined,
    };

    errorData['payload'] = req.body;
    errorData['url'] = req.url;
    errorData['apiError'] = apiError;
    errorData['httpDiagnostics'] = diagnostics;
    
    if(_.isNil(requestId) || _.isEmpty(requestId)) {
      this.loggerService.logException(errorData);
    }
    else {
      var logData = self.logDataService.getLogData(requestId);
      this.loggerService.logException(errorData, logData);
    }    
  }
}